import Select, {
  OptionProps,
  OptionTypeBase,
  Props,
  ValueType,
} from 'react-select'
import { useTranslation } from 'react-i18next'
import chroma from 'chroma-js'
import { useCallback } from 'react'
import IFormFieldProps from 'interfaces/form-field-props'
import { colourStyles } from 'helpers/react-select.helper'
import InputLabel from '../input-label/InputLabel'
import ErrorField from '../error-field/ErrorField'
import styles from './SelectInput.module.scss'

type ReasonText = string | null | undefined

interface ISelectInputProps<
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false
> extends Props<OptionType, IsMulti>,
    Omit<IFormFieldProps, 'name' | 'value'> {
  closeMenuOnSelect?: boolean
  withColors?: boolean
  disabled?: boolean
  cleareable?: boolean
  childTrim?: number
  disabledReason?: ReasonText | ((option: OptionType) => ReasonText)
  onChange?: (selected: ValueType<OptionType, IsMulti>) => void
}

const SelectInput = <
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false
>({
  name,
  childTrim = 0,
  cleareable = true,
  isMulti,
  value,
  placeHolder = '',
  label = undefined,
  options,
  withColors = false,
  onChange,
  closeMenuOnSelect,
  disabled = false,
  error = '',
  disabledReason,
  ...rest
}: ISelectInputProps<OptionType, IsMulti>) => {
  const colorProps: { styles?: any } = {}
  const { t } = useTranslation()

  const Option = useCallback(
    (props: OptionProps<OptionType, IsMulti>) => {
      const {
        data,
        label,
        isDisabled,
        isFocused,
        isSelected,
        innerRef,
        innerProps,
      } = props

      let bgColor = 'white'
      if (isFocused)
        bgColor = chroma(withColors ? data.color : '#2684ff')
          .alpha(0.1)
          .css()
      if (isSelected) bgColor = chroma('#2684ff').alpha(0.2).css()
      let child = data.child
      if (
        typeof child === 'string' &&
        childTrim > 0 &&
        child.length > childTrim + 6
      ) {
        child = child.substring(0, childTrim + 6) + ' (...)'
      }

      const disabledReasonText =
        typeof disabledReason === 'function'
          ? disabledReason(data)
          : disabledReason

      return (
        <div
          ref={innerRef}
          {...innerProps}
          style={{ background: bgColor }}
          className={styles.option}
        >
          <div className={isDisabled ? styles.disabled : styles.title}>
            {label} {isDisabled && disabledReasonText}
          </div>
          {child && <div className={styles.child}> {child} </div>}
        </div>
      )
    },
    [withColors, childTrim, disabledReason]
  )

  if (withColors && options) colorProps.styles = colourStyles
  return (
    <>
      {label && <InputLabel label={label} />}
      <Select<OptionType, IsMulti>
        classNamePrefix="react-select"
        value={value}
        onChange={(selected) => onChange?.(selected)}
        name={name}
        placeholder={placeHolder || t('commons.placeholders.select')}
        options={options}
        closeMenuOnSelect={closeMenuOnSelect}
        isMulti={isMulti}
        isDisabled={disabled}
        className={styles.text}
        isClearable={isMulti && cleareable}
        menuPosition="fixed"
        {...colorProps}
        isOptionDisabled={(option) => !!option.disabled}
        {...rest}
        components={{ Option, ...rest.components }}
      />
      {error && <ErrorField text={error} />}
    </>
  )
}

export default SelectInput
